// Duke University: public college page (#2652, batch 2), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs. Duke
// states a range ($103,180 to $103,975) by transportation; the low end is
// shown.
//  - Duke's Common Data Set 2025-2026 (CDS_PDF_2025-2026-20260622-website-
//    published-version.pdf from ir.provost.duke.edu): C1 applicants/admits/
//    enrollees for Fall 2025 (reported by sex; Duke leaves the residency
//    table and the early decision counts blank); C8 test policy for Fall
//    2027; C21 early decision dates; G1/G5 2026-2027 costs.
//  - admissions.duke.edu: the Apply page (2026-27 cycle deadlines, decision
//    plans, test-optional policy, Pratt coursework, arts supplement) and the
//    Resources + FAQs page (early decision terms, test-optional questions).
//  - College Scorecard, school id 198419 (net price by family income). The
//    API's DEMO_KEY was over its rate limit, so the figures were read from
//    the Scorecard's own school page, which shows the same latest fields
//    (checked against Princeton's API figures, which match it exactly).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Duke Common Data Set 2025-2026',
        url: 'https://ir.provost.duke.edu/sites/default/files/CDS_PDF_2025-2026-20260622-website-published-version.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Duke Undergraduate Admissions, Apply',
        url: 'https://admissions.duke.edu/apply/',
        read: '2026-10-01',
    },
    resources: {
        label: 'Duke Undergraduate Admissions, Resources and FAQs',
        url: 'https://admissions.duke.edu/resources/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?198419-Duke-University',
        read: '2026-10-15',
    },
    coa: {
        label: 'Duke Karsh Office of Undergraduate Financial Support, Cost of Attendance 2026-2027',
        url: 'https://financialaid.duke.edu/how-aid-calculated/cost-attendance/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'duke-university',
    kbId: 'duke_university',
    name: 'Duke University',
    shortName: 'Duke',
    location: 'Durham, NC',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Duke brand colors); decoration only.
    branding: {
        primary: '#012169',
        secondary: '#00539B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/duke_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 55541,
        admitted: 2910,
        enrolled: 1749,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Duke\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate for Trinity College or the Pratt School of Engineering.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set leaves its early decision counts blank, so it gives no early decision acceptance rate.',
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Trinity College of Arts & Sciences or the Pratt School of Engineering. A few things differ:',
        rows: [
            { school: 'Pratt School of Engineering', requirement: 'Calculus coursework is required, and physics is strongly recommended.' },
            { school: 'Arts supplement (optional)', requirement: 'One supplement in cinematic arts, dance, music, photography, theater or visual arts, evaluated by a Duke faculty member.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 2', decision: 'Mid-December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'Late March or early April', binding: false },
        ],
        note: 'These are Duke\'s 2026-27 cycle dates. Early Decision applicants also send first-quarter grades, and every applicant sends midyear grades by February 15.',
        source: 'apply',
        binding: 'Early Decision is binding, for students whose top choice is Duke. You may apply early action elsewhere, but not to a second binding program, and Duke asks you to honor any restrictive early action agreement. In rare cases, when a family cannot afford Duke, the student may be released from the commitment after talks with the financial support and admissions offices.',
        bindingSource: 'resources',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Duke is test-optional for first-year applicants in the 2026-2027 admissions cycle. If you apply without SAT or ACT scores, you are not at a disadvantage. If you send scores, they are considered. You can type your scores into the application; official scores are needed only if you enroll.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73740 },
            { item: 'Fees (estimated)', amount: 3019 },
            { item: 'Housing (double room)', amount: 11560 },
            { item: 'Food', amount: 10469 },
            { item: 'Books, course materials, supplies and equipment', amount: 536 },
            { item: 'Personal expenses', amount: 3274 },
            { item: 'Transportation (domestic, lowest estimate)', amount: 582 },
        ],
        total: 103180,
        note: 'First-year undergraduates. Transportation runs $582 to $1,318 depending on home address, so Duke states the total as $103,180 to $103,975. Pratt engineering students add $60 in dues.',
        source: 'coa',
        netPrice: {
            average: 23706,
            byIncome: [
                { income: '$0 to $30,000', amount: 6652 },
                { income: '$30,001 to $48,000', amount: null },
                { income: '$48,001 to $75,000', amount: 4608 },
                { income: '$75,001 to $110,000', amount: 16813 },
                { income: 'Over $110,000', amount: 48954 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $30,001 to $48,000 band. Your own price comes from Duke\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
